import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  AddIconComponent,
  MailIconComponent,
  WarningIconComponent,
} from '@kuiper/icons';
import { DepartmentDetails } from '../../departments/department-model';
import { UserData, UserDetails } from '../user-model';
import { BaseAssignedList } from '../../base/base-assigned-list';
import { Group } from '../../groups/group-model';
import { DualLangInputComponent } from '@areeb-innovative-technologies/angular_multi_languages_input_lib';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { delay, of } from 'rxjs';
import { UserWorkInfoFormSectionComponent } from './components/user-work-info-form-section/user-work-info-form-section.component';
import { PLACEMENT_TYPE_VALUES } from './components/user-work-info-form-section/user-work-info-form-section.constants';
import { Attachment } from '../../../../../../ui-components/lib/upload-files/attachment.interface';
import { ENDPOINTS } from '../../../../../common/constants/endpoints.constants';
import { FormValidation } from '../../../../../../ui-components/enums/form-validation';
import { InputComponent } from '../../../../../../ui-components/lib/input/input.component';
import { LoaderComponent } from '../../../../../../ui-components/lib/loader/loader.component';
import {
  LookupFilter,
  LookupOptionItem,
} from '../../../../../../ui-components/lib/lookup-field/lookup-field-item';
import { MobileInputComponent } from '../../../../../../ui-components/lib/mobile-input/mobile-input.component';
import { OptionItem } from '../../../../../../ui-components/lib/option.interface';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { UploadFilesComponent } from '../../../../../../ui-components/lib/upload-files/upload-files.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import { filterInputTypesEnum } from '../../../../../../ui-components/enums/filter-input-type';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { regexVariables } from '../../../../../common/constants/regex.constant';
import { GroupUserCardComponent } from '../../../../../common/components/group-user-card/group-user-card.component';
import { Option } from '../../../../../common/models/option.model';
import { ApiResponse } from '../../../../../common/models/api-response.model';
import { uploadFileUrls } from '../../../../../common/constants/upload-file-url';
import { ScrollToErrorDirective } from '../../../../../common/directives/scroll-to-error/scroll-to-error.directive';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { AuthService } from '../../../../../common/services/auth/auth.service';
import { AccountSettingsService } from '../../../../employee-flow/account-settings/account-settings.service';
import { TenantPathPipe } from '../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { AssignGroupUsersComponent } from '../../../../../common/components/assign-group-users/assign-group-users.component';
import { dualLanguageCustomValidators } from '../../../../../../form-builder/services/form/custom-validators';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../../common/constants/feature-flags.constants';

@Component({
  selector: 'lib-user-form',
  imports: [
    OverlayLoaderComponent,
    InputComponent,
    ValidationMessageComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    GroupUserCardComponent,
    AssignGroupUsersComponent,
    LoaderComponent,
    PaginatorComponent,
    MobileInputComponent,
    UploadFilesComponent,
    DualLangInputComponent,
    RouterLink,
    ScrollToErrorDirective,
    AddIconComponent,
    MailIconComponent,
    WarningIconComponent,
    TenantPathPipe,
    UserWorkInfoFormSectionComponent,
  ],
  templateUrl: './user-form.component.html',
  styleUrl: './user-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserFormComponent extends BaseAssignedList {
  readonly ADMIN_ROUTES = ADMIN_ROUTES;

  private readonly toasterService = inject(ToasterService);
  private readonly authService = inject(AuthService);
  private readonly accountSettingsService = inject(AccountSettingsService);
  private readonly featureFlagService = inject(FeatureFlagsService);
  tenantService = inject(TenantService);
  readonly destroyRef = inject(DestroyRef);

  private readonly isUserPlacementTypesEnabled =
    this.featureFlagService.getFeatureFlagValue(
      FEATURE_FLAGS.ADMIN.USER_MANAGEMENT.user_placement_types
    );

  scrollToError = viewChild.required(ScrollToErrorDirective);
  assignGroupUsersDialog = viewChild<TemplateRef<unknown>>(
    'assignGroupUsersDialog'
  );
  pageTop = viewChild<ElementRef<HTMLDivElement>>('pageTop');
  addUserModal = viewChild<TemplateRef<unknown>>('addUser');
  externalUserChangeModal =
    viewChild<TemplateRef<unknown>>('externalUserChange');

  userFormGroup = signal<FormGroup>(new FormGroup({}));
  departmentDetails!: DepartmentDetails;
  legalEntity = signal<OptionItem[]>([]);
  department = signal<OptionItem[]>([]);
  directManager = signal<OptionItem[]>([]);
  groups = signal<OptionItem[]>([]);
  legalEntityId = signal<number>(0);

  userDetails = signal<UserDetails | undefined>(undefined);
  userId = signal<string>('');
  emailDisabled = signal<boolean>(false);
  userData = signal<UserData | undefined | null>(null);

  departmentApi = ENDPOINTS.Lookup.Departments;
  departmentFilter: LookupFilter[] = [
    {
      key: 'legal_entity_id',
      value: 'legal_entity_id',
      value_type: filterInputTypesEnum.DYNAMIC,
      multiple: false,
      required: true,
    },
  ];
  selectedDepartment = signal<LookupOptionItem | undefined>(undefined);
  selectedDirectManager = signal<LookupOptionItem | undefined>(undefined);

  jobTitleApi = ENDPOINTS.Lookup.JobTitles;
  selectedJobTitle = signal<LookupOptionItem | undefined>(undefined);

  positionApi = ENDPOINTS.Lookup.Positions;
  selectedPosition = signal<LookupOptionItem | undefined>(undefined);

  selectedCountryISO = signal<string>('');
  selectedPhoneNumber = signal<string>('');

  uploadFileUrls = uploadFileUrls;
  uploadedImage = signal<Attachment[]>([]);

  constructor() {
    super();

    this.getLegalEntities();
    this.createForm();
    this.handleRouteParams();
  }

  getLegalEntities() {
    this.userService
      .getLegalEntities()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: { data: any }) => {
        this.legalEntity.set(response.data);
      });
  }

  createForm() {
    this.userFormGroup.set(
      new FormGroup({
        image: new FormControl(''),
        first_name: new FormGroup(
          {
            en: new FormControl('', {
              validators: [Validators.maxLength(FormValidation.maxLength)],
            }),
            ar: new FormControl('', {
              validators: [Validators.maxLength(FormValidation.maxLength)],
            }),
          },
          {
            validators: [dualLanguageCustomValidators.atLeastOneLangRequired],
          }
        ),
        last_name: new FormGroup(
          {
            en: new FormControl('', {
              validators: [Validators.maxLength(FormValidation.maxLength)],
            }),
            ar: new FormControl('', {
              validators: [Validators.maxLength(FormValidation.maxLength)],
            }),
          },
          {
            validators: [dualLanguageCustomValidators.atLeastOneLangRequired],
          }
        ),
        phone: new FormControl('', {
          validators: [
            Validators.required,
            Validators.pattern(regexVariables.phone),
          ],
        }),
        email: new FormControl('', {
          validators: [Validators.required, Validators.email],
        }),
        identifier: new FormControl('', [
          Validators.maxLength(FormValidation.identifierMaxLength),
        ]),
        placement_type: new FormControl<string | null>(null, [
          ...(this.isUserPlacementTypesEnabled ? [Validators.required] : []),
        ]),
        department_id: new FormControl<number | null>(null),
        job_title_id: new FormControl<number | null>(null),
        direct_manager_id: new FormControl<number | null>(null),
        position_id: new FormControl(null),
      })
    );

    this.userFormGroup().addControl(
      'position',
      new FormGroup({
        position_id: new FormControl({ value: '', disabled: true }),
        legal_entity_id: new FormControl({ value: '', disabled: true }),
        department_id: new FormControl({ value: '', disabled: true }),
        job_title_id: new FormControl({ value: '', disabled: true }),
        direct_manager_id: new FormControl({ value: '', disabled: true }),
      })
    );
  }

  submit() {
    this.formService.syncDualLangControls(this.userFormGroup());

    if (this.userFormGroup().invalid) {
      this.userFormGroup().markAllAsTouched();
      this.scrollToError().scrollToFirstError();
      return;
    }

    const userForm = this.prepareUserForm();
    if (this.editMode() && this.isExternalUserBecomingRegular()) {
      this.openExternalUserChangeModal();
    } else if (this.editMode()) {
      this.updateUser(userForm);
    } else {
      this.openModal();
    }
  }

  /** An External user given a position or a department and job title becomes a regular user, so the admin confirms it first. */
  isExternalUserBecomingRegular(): boolean {
    const selectedType = this.userFormGroup().get('placement_type')?.value;

    return (
      this.userDetails()?.placement?.type?.value ===
        PLACEMENT_TYPE_VALUES.EXTERNAL &&
      (selectedType === PLACEMENT_TYPE_VALUES.VACANT_POSITION ||
        selectedType === PLACEMENT_TYPE_VALUES.DEPARTMENT_AND_JOB_TITLE)
    );
  }

  externalUserChangeMessage(): string {
    return this.userFormGroup().get('placement_type')?.value ===
      PLACEMENT_TYPE_VALUES.VACANT_POSITION
      ? 'user_form_screen_external_to_position_confirm_message'
      : 'user_form_screen_external_to_department_confirm_message';
  }

  confirmExternalUserChange() {
    this.closeModal();
    this.updateUser(this.prepareUserForm());
  }

  addDraftUser(actionType: string) {
    const userForm = this.prepareUserForm();
    const draftUserForm = { ...userForm, state: actionType };
    this.addUser(draftUserForm);
  }

  prepareUserForm() {
    const raw = this.userFormGroup().getRawValue();
    const isDepartmentAndJobTitle =
      raw.placement_type === PLACEMENT_TYPE_VALUES.DEPARTMENT_AND_JOB_TITLE;
    const isVacantPosition =
      raw.placement_type === PLACEMENT_TYPE_VALUES.VACANT_POSITION;

    return {
      ...raw,
      department_id: isDepartmentAndJobTitle ? raw.department_id : null,
      job_title_id: isDepartmentAndJobTitle ? raw.job_title_id : null,
      direct_manager_id: isDepartmentAndJobTitle ? raw.direct_manager_id : null,
      // When placement types are off, the fallback single-Position picker writes
      // straight to `position_id` with no `placement_type` to gate it by.
      position_id: this.isUserPlacementTypesEnabled
        ? isVacantPosition
          ? raw.position_id
          : null
        : raw.position_id,
      image: this.userFormGroup().get('image')?.value
        ? this.userFormGroup().get('image')?.value[0]
        : null,
      added_groups: this.addedList().length
        ? this.addedList().map(item => +item.value)
        : [],
      removed_groups: this.removedList().length
        ? this.removedList().map(item => +item.value)
        : [],
    };
  }

  handleRouteParams() {
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.setTextBasedOnId(params['id']);

        if (params['id']) {
          this.userId.set(params['id']);
          this.getUserDetails();
          this.getAssignedCardList();
        } else {
          this.isLoading.set(false);
        }
      });
  }

  setTextBasedOnId(paramsId: string) {
    this.pageTitle.set(
      paramsId
        ? this.transloco.translate('user_form_screen_edit_title')
        : this.transloco.translate('user_form_screen_add_title')
    );
    this.buttonText.set(
      paramsId
        ? this.transloco.translate('user_form_screen_save_edits_button')
        : this.transloco.translate('user_form_screen_add_user_button')
    );
  }

  getUserDetails() {
    this.userService
      .getUserDetails(this.userId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) =>
          this.handleUserDetailsSuccessResponse(response),
        error: () => {
          this.isLoading.set(false);
          this.router.navigate([
            this.tenantService.adminPath(`${ADMIN_ROUTES.USERS_DATABASE.LIST}`),
          ]);
        },
      });
  }

  handleUserDetailsSuccessResponse(response: any) {
    this.userDetails.set(response.data);
    this.userData.set(response.data);
    this.editMode.set(true);
    this.selectedCountryISO.set(response.data.phone?.country ?? '');
    this.selectedPhoneNumber.set(
      this.userDetails()?.phone?.normalized_number ?? ''
    );
    this.patchForm(response.data);
    this.handleEmailDisability();
  }

  getAssignedCardList() {
    this.isAssignedListLoading.set(true);
    this.userService
      .getUserGroups(this.userId(), this.assignedListCurrentPage(), 15)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: ApiResponse<Group>) =>
          this.handleUserAssignedGroupsResponse(response),
      });
  }

  handleUserAssignedGroupsResponse(response: ApiResponse<Group>) {
    this.assignedList.set(this.mapAssignedGroups(response.data));
    this.handleAssignedListResponse(response.meta);
  }

  mapAssignedGroups(groups: Group[]): Option[] {
    return groups
      .map(group => {
        const groupOption: Option = {
          label: group.name,
          value: group.id.toString(),
          image: group.image,
        };
        return groupOption;
      })
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
  }

  handleAssignedListPagination(pageNumber: number) {
    this.assignedListCurrentPage.set(pageNumber);
    this.getAssignedCardList();
  }

  patchForm(data: UserDetails) {
    of(null)
      .pipe(delay(10), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.userFormGroup().patchValue({
          first_name: {
            en: data.translations.first_name.en,
            ar: data.translations.first_name.ar,
          },
          last_name: {
            en: data.translations.last_name.en,
            ar: data.translations.last_name.ar,
          },
          phone: data.phone?.international_number ?? '',
          email: data.email,
          identifier: data.identifier,
          position_id:
            data.placement?.position?.id ?? data.position?.id ?? null,
        });

        this.patchWorkInfo(data);

        if (data.image?.url) {
          this.userFormGroup().get('image')?.setValue([data.image.id]);
          this.uploadedImage.set([data.image as Attachment]);
        }
        this.isLoading.set(false);
      });

    this.scrollToTop();
  }

  patchWorkInfo(data: UserDetails) {
    const position = data.placement?.position ?? data.position;
    const directManager = data.placement?.direct_manager ?? data.direct_manager;

    if (position) {
      this.selectedDepartment.set({
        value: position.department?.id ?? 0,
        label: position.department?.name ?? '',
      });
      this.selectedDirectManager.set({
        value: directManager?.value ?? 0,
        label: directManager?.label ?? '',
      });
      this.selectedJobTitle.set({
        value: position.job_title?.id ? Number(position.job_title.id) : 0,
        label: position.job_title?.name ?? '',
      });

      this.selectedPosition.set({
        id: position.id ?? 0,
        name: position.name ?? '',
        value: String(position.id),
        label: position.name ?? '',
        legal_entity_name: position.legal_entity?.name ?? '',
        department_name: position.department?.name ?? '',
        job_title_name: position.job_title?.name ?? '',
        direct_manager_name: directManager?.label ?? '',
      });

      this.userFormGroup()
        .get('position')
        ?.patchValue({
          position_id: position.id ?? 0,
          legal_entity_id: position.legal_entity?.id ?? 0,
          department_id: position.department?.id ?? 0,
          job_title_id: position.job_title?.id ?? 0,
          direct_manager_id: directManager?.value ?? 0,
        });
    }

    this.patchPlacement(data);
  }

  /** Patches the live placement-type controls from the consolidated `placement` field, falling back to the legacy `position`/`direct_manager` shape. */
  patchPlacement(data: UserDetails) {
    const placement = data.placement;
    const placementType =
      placement?.type?.value ??
      (data.position
        ? PLACEMENT_TYPE_VALUES.VACANT_POSITION
        : data.work_info
          ? PLACEMENT_TYPE_VALUES.DEPARTMENT_AND_JOB_TITLE
          : null);

    const departmentId =
      placement?.department?.id ?? data.work_info?.department?.id;
    const jobTitleId =
      placement?.job_title?.id ?? data.work_info?.job_title?.id;
    const directManagerId =
      placement?.direct_manager?.value ?? data.work_info?.direct_manager?.id;

    this.userFormGroup().patchValue({
      placement_type: placementType,
      department_id: departmentId ? Number(departmentId) : null,
      job_title_id: jobTitleId ? Number(jobTitleId) : null,
      direct_manager_id: directManagerId ? Number(directManagerId) : null,
    });

    if (placementType === PLACEMENT_TYPE_VALUES.DEPARTMENT_AND_JOB_TITLE) {
      this.selectedDepartment.set({
        value: departmentId ? Number(departmentId) : 0,
        label:
          placement?.department?.name ?? data.work_info?.department?.name ?? '',
      });
      this.selectedJobTitle.set({
        value: jobTitleId ? Number(jobTitleId) : 0,
        label:
          placement?.job_title?.name ?? data.work_info?.job_title?.name ?? '',
      });
      this.selectedDirectManager.set({
        value: directManagerId ? Number(directManagerId) : 0,
        label:
          placement?.direct_manager?.label ??
          data.work_info?.direct_manager?.name ??
          '',
      });
    }
  }

  handleEmailDisability() {
    this.emailDisabled.set(
      this.userDetails()?.state?.value === 'inactive' ||
        this.userDetails()?.state?.value === 'active'
    );
    const emailControl = this.userFormGroup().get('email') as FormControl;
    if (this.emailDisabled()) {
      emailControl.disable();
    } else {
      emailControl.enable();
    }
  }

  updateUser(userForm: any) {
    this.isLoading.set(true);
    this.userService
      .updateUser(userForm, this.userDetails()?.id?.toString() ?? '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.syncCurrentUserProfile();
          this.handleSuccess();
        },
        error: error => this.handleError(error),
      });
  }

  /** If the admin edited their own account, refresh the header badge. */
  private syncCurrentUserProfile() {
    if (!this.authService.isCurrentUser(this.userDetails()?.email)) return;
    this.accountSettingsService.refreshCurrentUser().subscribe();
  }

  addUser(userForm: any) {
    this.isLoading.set(true);
    this.userService
      .addUser(userForm)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.handleSuccess();
          this.closeModal();
        },
        error: error => this.handleError(error),
      });
  }

  handleError(error: any) {
    this.closeModal();
    this.isLoading.set(false);
    this.userFormGroup().markAllAsTouched();
    this.formService.addParentFormGroupErrors(error, this.userFormGroup());
    this.toasterService.add(
      error.message ? error.message : error.error?.message,
      '',
      '',
      'error',
      true,
      true
    );
    this.scrollToError().scrollToFirstError(false);
  }

  openAssignGroupUsersDialog() {
    this.currentPage.set(1);
    this.selectedList.set([]);
    this.getGroupUsers();

    this.modalService.modalContent.set({
      isOpen: true,
      customizeContent: this.assignGroupUsersDialog(),
      canNotCloseFromOutside: true,
      width: '51',
    });
  }

  getGroupUsers(name = '', page = 1) {
    this.currentPage.set(page);
    this.isLoadingGroupUsers.set(true);
    const showActiveGroupsOnly = true;

    this.groupService
      .groupLookup(
        name,
        this.currentPage(),
        showActiveGroupsOnly,
        this.userId(),
        25
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => this.handleUsersLookupResponse(response, name));
  }

  selectItem(item: Option) {
    this.baseSelectItem(item);

    if (
      this.currentPage() < this.totalPages() &&
      this.lookupList().length < 7
    ) {
      this.getGroupUsers('', this.currentPage() + 1);
    }
  }

  scrollToTop() {
    (<ElementRef<HTMLDivElement>>this.pageTop()).nativeElement.scrollIntoView({
      behavior: 'smooth',
    });
  }

  openModal() {
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: this.addUserModal(),
      width: '24',
    };

    this.modalService.modalContent.set(modal);
  }

  openExternalUserChangeModal() {
    this.modalService.modalContent.set({
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: this.externalUserChangeModal(),
      width: '24',
    });
  }

  navigateToList() {
    this.router.navigate([
      this.tenantService.adminPath(`${ADMIN_ROUTES.USERS_DATABASE.LIST}`),
    ]);
  }

  navigateToDetails() {
    this.router.navigate([
      this.tenantService.adminPath(`${ADMIN_ROUTES.USERS_DATABASE.DETAILS}`),
      this.userId(),
    ]);
  }

  get cancelRoute(): string {
    return this.editMode()
      ? this.tenantService.adminPath(
          `${ADMIN_ROUTES.USERS_DATABASE.DETAILS}/${this.userId()}`
        )
      : this.tenantService.adminPath(`${ADMIN_ROUTES.USERS_DATABASE.LIST}`);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }
}
